import React from 'react';
import { RefreshCw, ExternalLink } from 'lucide-react';
import HealthCard from '../components/ui/HealthCard';
import { useHealthChecks } from '../hooks/useHealthChecks';

const AuthDebug: React.FC = () => {
  const {
    healthStatus,
    healthDependencies,
    databaseHealth,
    isLoading,
    lastUpdated,
    refreshAll,
    refreshStatus,
    refreshDependencies,
    refreshDatabase
  } = useHealthChecks();

  return (
    <div className="max-w-6xl mx-auto">
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-white mb-2">Service Health</h1>
            <p className="text-gray-400">System health monitoring and authentication debugging</p>
          </div>
          <button
            onClick={refreshAll}
            disabled={isLoading}
            className="flex items-center space-x-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
            <span>Refresh All</span>
          </button>
        </div>
      </div>
      
      <div className="space-y-8">
        {/* System Health Section */}
        <div>
          <h2 className="text-xl font-semibold text-white mb-6">System Health</h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
            {/* Overall Service Health */}
            <HealthCard
              title="Service Status"
              status={healthStatus ? healthStatus.status : (isLoading ? 'loading' : 'unknown')}
              message={healthStatus ? `${healthStatus.service} v${healthStatus.version}` : 'Unable to connect to service'}
              details={healthStatus ? [
                { label: 'Environment', value: healthStatus.environment },
                { label: 'Version', value: healthStatus.version },
                { label: 'Service', value: healthStatus.service }
              ] : undefined}
              lastUpdated={healthStatus?.timestamp || lastUpdated || undefined}
              onRefresh={refreshStatus}
              isRefreshing={isLoading}
            />

            {/* Database Health */}
            <HealthCard
              title="Database"
              status={databaseHealth ? databaseHealth.status : (isLoading ? 'loading' : 'unknown')}
              message={databaseHealth ? 
                (databaseHealth.database.connected ? 'Database connection healthy' : 'Database connection issues') : 
                'Unable to check database status'
              }
              details={databaseHealth ? [
                { 
                  label: 'Connected', 
                  value: databaseHealth.database.connected ? 'Yes' : 'No',
                  status: databaseHealth.database.connected ? 'healthy' : 'unhealthy'
                },
                { 
                  label: 'Response Time', 
                  value: `${databaseHealth.database.responseTime}ms`,
                  status: databaseHealth.database.responseTime < 100 ? 'healthy' : 
                          databaseHealth.database.responseTime < 500 ? 'warning' : 'unhealthy'
                },
                ...(databaseHealth.database.recordCount !== undefined ? [
                  { label: 'Record Count', value: databaseHealth.database.recordCount.toString() }
                ] : [])
              ] : undefined}
              lastUpdated={databaseHealth?.timestamp || lastUpdated || undefined}
              onRefresh={refreshDatabase}
              isRefreshing={isLoading}
            />

            {/* Dependencies Health */}
            <HealthCard
              title="Dependencies"
              status={healthDependencies ? healthDependencies.status : (isLoading ? 'loading' : 'unknown')}
              message={healthDependencies ? 'External service dependencies' : 'Unable to check dependencies'}
              details={healthDependencies ? [
                { 
                  label: 'Database', 
                  value: healthDependencies.dependencies.database.message,
                  status: healthDependencies.dependencies.database.status
                },
                { 
                  label: 'Key Vault', 
                  value: healthDependencies.dependencies.keyVault.message,
                  status: healthDependencies.dependencies.keyVault.status
                },
                { 
                  label: 'App Insights', 
                  value: healthDependencies.dependencies.appInsights.message,
                  status: healthDependencies.dependencies.appInsights.status
                }
              ] : undefined}
              lastUpdated={healthDependencies?.timestamp || lastUpdated || undefined}
              onRefresh={refreshDependencies}
              isRefreshing={isLoading}
            />
          </div>
        </div>

        {/* Authentication Debug Section */}
        <div>
          <h2 className="text-xl font-semibold text-white mb-6">Authentication Debug</h2>
          
          <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
            <h3 className="text-lg font-medium text-gray-200 mb-4">SharePoint Embedded Diagnostics</h3>
            <p className="text-gray-400 mb-4">
              For comprehensive SharePoint Embedded container and permission testing, use the dedicated diagnostic page.
            </p>
            <a 
              href="/debug/spe" 
              className="inline-flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <ExternalLink className="w-4 h-4" />
              <span>Open SharePoint Embedded Diagnostics</span>
            </a>
          </div>
          
          <div className="bg-yellow-900/20 border border-yellow-500/30 rounded-lg p-4 mt-6">
            <h3 className="text-yellow-400 font-medium mb-2">⚠️ Development Only</h3>
            <p className="text-yellow-300 text-sm">
              This debug page is for development and testing purposes only. 
              It will be removed before production deployment.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthDebug;
